/**
 * App Shell
 *
 * Main application component with routing and authentication.
 *
 * Requirements:
 * - FR-001: Authenticate users via Microsoft Entra ID
 * - FR-002: Support roles: admin, operator, viewer
 * - FR-003: Reject unauthenticated requests
 */

import React, { useEffect } from 'react';
import clsx from 'clsx';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { msalInstance } from './auth/config';
import { validateConfig, isDevAuthBypass, apiTokenRequest } from './authConfig';
import { getDevUserRole, setDevUserRole, clearDevUserRole } from './auth/devUserStore';
import DevLoginPage from './pages/auth/DevLoginPage';
import LoginPage from './pages/auth/LoginPage';
import { apiClient } from './modules/backup/services/api';
import { setupAuthInterceptor } from './services/apiClient';
import { useAuth } from './auth/hooks/useAuth';
import { LicenseGate } from './auth/LicenseGate';
import { useAuthStore } from './stores/authStore';
import { useEnvironment } from './modules/backup/hooks/useEnvironment';
import { useCurrentTenant } from './hooks/useCurrentTenant';
import { EnvironmentBadge } from './modules/backup/components/common/EnvironmentBadge';

// User Preferences (Feature 035)
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import { useUserPreferences } from './hooks/useUserPreferences';
import { ExtensibleNavigation } from './components/Navigation/ExtensibleNavigation';
import { GlobalSearch } from './components/Search/GlobalSearch';
import { useGlobalSearch, useEnhancedKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { BreadcrumbProvider, Breadcrumb } from './components/Breadcrumb';
import { useBreadcrumbContext } from './components/Breadcrumb/useBreadcrumbContext';
import { PreferencesPanel } from './components/UserPreferences/PreferencesPanel';
import { MobileNavigation } from './components/Navigation/MobileNavigation';
import { KeyboardShortcutsHelp } from './components/Search/KeyboardShortcutsHelp';
import { NotificationBell } from './components/Notification/NotificationBell';
import { AppTooltip } from './components/ui/AppTooltip';
import { DevRoleSwitcher } from './components/dev/DevRoleSwitcher';
import { DevUserSwitcher } from './components/dev/DevUserSwitcher';
import { ThemeProvider } from './theme/ThemeProvider';

// Auth pages (Feature 033)
import { LoginCallback } from './pages/auth/LoginCallback';
import { Unauthorized } from './pages/auth/Unauthorized';

// Spec 216 T003 — the ONE shared child-route tree (was duplicated dev/prod)
import { appChildRoutes } from './AppRoutes';

// Shell-level pieces still owned by App.tsx
import { GlobalBackupActivityDrawer } from './modules/backup/components/GlobalBackupActivityDrawer';
import GlobalBackupErrorDrawer from '@/modules/backup/components/errors/GlobalBackupErrorDrawer';
import { NotFound, ServiceUnavailable } from './pages/error';
import { PendingApproval } from './pages/auth/PendingApproval';

// Validate configuration on startup
validateConfig();

// MSAL instance is created in auth/config.ts and initialized in main.tsx
// We import the singleton here to ensure we use the same instance everywhere

// Setup authentication interceptor for the centralized API client (services/apiClient)
// This enables dashboardApi and other services to make authenticated API calls
setupAuthInterceptor(msalInstance, apiTokenRequest);

// Create a query client instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 seconds
    },
  },
});

/**
 * Loading spinner component
 */
const LoadingSpinner: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => (
  <div className='min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900'>
    <div className='text-center'>
      <div className='animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4' />
      <p className='text-gray-600'>{message}</p>
    </div>
  </div>
);

/**
 * Error boundary fallback
 * @remarks Reserved for future error boundary implementation
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ErrorFallback: React.FC<{ error?: Error }> = ({ error }) => (
  <div className='min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900'>
    <div className='text-center max-w-md mx-auto p-6'>
      <div className='text-red-500 text-5xl mb-4'>⚠️</div>
      <h1 className='text-2xl font-bold text-gray-800 mb-2'>Something went wrong</h1>
      <p className='text-gray-600 mb-4'>{error?.message || 'An unexpected error occurred'}</p>
      <button
        onClick={() => window.location.reload()}
        className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'
      >
        Refresh Page
      </button>
    </div>
  </div>
);

/**
 * Protected route wrapper
 */
const ProtectedRoute: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessToken } = useAuth();
  const updateActivity = useAuthStore((state) => state.updateActivity);

  // Set up API client with token provider
  useEffect(() => {
    apiClient.setTokenProvider(getAccessToken);
  }, [getAccessToken]);

  // Track user activity
  useEffect(() => {
    const handleActivity = (): void => updateActivity();

    window.addEventListener('click', handleActivity);
    window.addEventListener('keypress', handleActivity);

    return () => {
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keypress', handleActivity);
    };
  }, [updateActivity]);

  if (isLoading) {
    return <LoadingSpinner message='Authenticating...' />;
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' replace />;
  }

  return <>{children || <Outlet />}</>;
};

/**
 * App layout with navigation (authenticated users)
 */
const AppLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const { environmentInfo, version } = useEnvironment();
  const { tenant: currentTenant } = useCurrentTenant();
  const { sidebarCollapsed, toggleSidebar, setSidebarCollapsed } = useUserPreferences();
  const { isOpen: searchOpen, openSearch, closeSearch } = useGlobalSearch();
  const { showHelp, toggleHelp } = useEnhancedKeyboardShortcuts();
  const { breadcrumbs } = useBreadcrumbContext();
  const [preferencesOpen, setPreferencesOpen] = React.useState(false);
  const [preferencesInitialSection, setPreferencesInitialSection] = React.useState<
    'general' | 'email' | 'favorites' | undefined
  >(undefined);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  const [isCompact, setIsCompact] = React.useState(false);

  // Spec 253: an email "manage preferences" deep-link (?prefs=email) opens the
  // User Preferences modal on the Email tab, then cleans the URL.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('prefs') === 'email') {
      setPreferencesInitialSection('email');
      setPreferencesOpen(true);
      params.delete('prefs');
      params.delete('category');
      params.delete('target');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, []);

  // Handle responsive breakpoints
  // < 768px: hide sidebar completely (mobile)
  // < 1024px: collapse sidebar (compact)
  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const compact = width < 1024;
      
      setIsMobile(mobile);
      setIsCompact(compact);
      
      // Auto-collapse sidebar at medium widths
      if (compact && !mobile && !sidebarCollapsed) {
        setSidebarCollapsed(true);
      }
      
      if (!mobile && mobileNavOpen) {
        setMobileNavOpen(false);
      }
    };

    handleResize(); // Check initial size
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileNavOpen, sidebarCollapsed, setSidebarCollapsed]);

  return (
    <div className='h-screen bg-[var(--color-bg-page)] flex flex-col overflow-hidden'>
      {/* Header */}
      <header className='bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 flex-shrink-0'>
          <div className='px-4 sm:px-6 lg:px-8 py-2'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              {/* Left side: hamburger + logo + title */}
              <div className='flex items-center space-x-3 flex-shrink-0'>
                {/* Mobile hamburger menu */}
                <button
                  onClick={() => setMobileNavOpen(true)}
                  className='md:hidden p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md min-h-[44px] min-w-[44px] flex items-center justify-center'
                  aria-label='Open navigation menu'
                >
                  <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6h16M4 12h16M4 18h16' />
                  </svg>
                </button>
                
                {/* Logo */}
                <img src='/logo.png' alt='Logo' className='w-[200px] h-auto object-contain hidden lg:block' />
                <img src='/logo.png' alt='Logo' className='w-[120px] h-auto object-contain lg:hidden' />
                <span className='text-xl font-bold text-brand hidden lg:inline'>Fabric Control Center</span>
                <span className='text-base font-bold text-brand hidden sm:inline lg:hidden'>Control Center</span>
                {/*
                  * The tenant whose data this session is reading. Resolved per request from the
                  * login → dbo.users association, never from a build-time or process-wide value:
                  * one deployment serves every tenant, so a global value would label every
                  * customer identically. Rendered only when known — an unnamed tenant shows
                  * nothing rather than a placeholder that could be mistaken for a real name.
                  */}
                {currentTenant?.name && (
                  <span
                    data-testid='header-tenant-name'
                    title={`Tenant: ${currentTenant.name}`}
                    className='hidden sm:inline text-sm font-medium text-[var(--color-text-secondary)] border-l border-[var(--color-border)] pl-2 sm:pl-3 ml-1 truncate max-w-[12rem]'
                  >
                    {currentTenant.name}
                  </span>
                )}
              </div>

              {/* Right side: search + user menu - wraps on smaller screens */}
              <div className='flex items-center flex-wrap justify-end gap-2 sm:gap-4 ml-auto'>
                {/* Global Search Button */}
                <button
                  onClick={openSearch}
                  className='flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors'
                  title='Search (Cmd+K)'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
                  </svg>
                  <span className='hidden lg:inline'>Search</span>
                  <kbd className='hidden lg:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded'>
                    <span>⌘</span>K
                  </kbd>
                </button>

                {/* Environment Badge — only shown in non-production environments */}
                {environmentInfo && environmentInfo.environment !== 'production' && (
                  <div className='hidden sm:block'>
                    <AppTooltip title='Current deployment environment and connected infrastructure'>
                      <div>
                        <EnvironmentBadge
                          environment={environmentInfo.environment}
                          details={{
                            database: environmentInfo.database,
                            sqlMi: environmentInfo.sqlMi,
                            keyVault: environmentInfo.keyVault,
                          }}
                          size='md'
                        />
                      </div>
                    </AppTooltip>
                  </div>
                )}

                {/* Notification Bell */}
                <AppTooltip title='View system notifications and alerts'>
                  <div>
                    <NotificationBell enabled={true} />
                  </div>
                </AppTooltip>

                {/* Spec 216 T010: the user name opens their preferences (was inert text) */}
                <button
                  onClick={() => setPreferencesOpen(true)}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:underline hidden md:inline'
                  title='Open your preferences'
                >
                  {user?.displayName}
                </button>
                <AppTooltip title={`Your role: ${user?.role}. Determines which features and actions are available.`}>
                  <span className='px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 rounded capitalize'>
                    {user?.role}
                  </span>
                </AppTooltip>
                {/* Dev-only: assume another role / act as another user to test multi-user flows */}
                <DevRoleSwitcher />
                <DevUserSwitcher />

                <button
                  onClick={() => setPreferencesOpen(true)}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:underline flex items-center gap-1'
                  title='User Preferences'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                  </svg>
                  <span className='hidden sm:inline'>Settings</span>
                </button>
                <button
                  onClick={() => {
                    if (isDevAuthBypass) {
                      clearDevUserRole();
                      window.location.reload();
                    } else {
                      logout();
                    }
                  }}
                  className='text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:underline'
                >
                  <span className='hidden sm:inline'>Sign out</span>
                  <svg className='w-4 h-4 sm:hidden' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1' />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Middle section: sidebar + content */}
        <div className='flex flex-1 min-h-0 overflow-hidden'>
          {/* Sidebar navigation - hidden on mobile */}
          <div className={clsx('flex-shrink-0', isMobile && 'hidden')}>
            <ExtensibleNavigation
              collapsed={sidebarCollapsed}
              onCollapsedChange={toggleSidebar}
              onUserClick={() => setPreferencesOpen(true)}
            />
          </div>

          {/* Content area */}
          <div className='flex-1 flex flex-col min-w-0 overflow-hidden'>
            {/* Breadcrumb Navigation */}
            <div className='flex-shrink-0'>
              <Breadcrumb breadcrumbs={breadcrumbs} />
            </div>

            {/* Main content */}
            <main className='flex-1 overflow-y-auto overflow-x-hidden'>
              <Outlet />
            </main>
          </div>
        </div>

        {/* Footer with version */}
        <footer className='bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-2 flex-shrink-0'>
          <div className='flex justify-between items-center text-xs text-gray-500 dark:text-gray-400'>
            <span>© 2026 Dobler Data Solutions</span>
            <div className='flex items-center space-x-4'>
              {/* Spec 216 T010: version only — deploy timestamp removed */}
              <span>{version || '(dev)'}</span>
            </div>
          </div>
        </footer>

      {/* Global Search Command Palette */}
      <GlobalSearch 
        isOpen={searchOpen}
        onClose={closeSearch}
      />

      {/* User Preferences Panel */}
      <PreferencesPanel
        isOpen={preferencesOpen}
        onClose={() => {
          setPreferencesOpen(false);
          setPreferencesInitialSection(undefined);
        }}
        initialSection={preferencesInitialSection}
      />

      {/* Mobile Navigation Drawer */}
      <MobileNavigation 
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsHelp
        isOpen={showHelp}
        onClose={toggleHelp}
      />

      {/* Global backup/restore activity drawer — auto-opens when a backup is launched
          from anywhere in the app (e.g. a tier change on a catalog, outside /backup). */}
      <GlobalBackupActivityDrawer />
      <GlobalBackupErrorDrawer />
    </div>
  );
};

/**
 * Main App Component
 */
const App: React.FC = () => {
  // In dev bypass mode, render with same AppLayout but skip MSAL provider
  if (isDevAuthBypass) {
    return (
      <QueryClientProvider client={queryClient}>
        <UserPreferencesProvider>
          <ThemeProvider>
            <BrowserRouter>
              <BreadcrumbProvider>
                <DevAuthGuard>
                <Routes>
                  {/* All routes accessible in dev mode — same layout as production.
                      Spec 216 T003: the shared child tree lives in AppRoutes.tsx. */}
                  <Route element={<LicenseGate><AppLayout /></LicenseGate>}>
                    <Route path='/login' element={<Navigate to='/dashboard' replace />} />
                    {appChildRoutes}
                  </Route>

                  {/* 404 */}
                  <Route path='*' element={<NotFound />} />
                </Routes>
                </DevAuthGuard>
              </BreadcrumbProvider>
            </BrowserRouter>
          </ThemeProvider>
        </UserPreferencesProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <UserPreferencesProvider>
        <ThemeProvider>
            <BrowserRouter>
              <BreadcrumbProvider>
                <Routes>
                  {/* Public routes */}
                  <Route path='/login' element={<LoginPage />} />
                  <Route path='/auth/callback' element={<LoginCallback />} />
                  <Route path='/unauthorized' element={<Unauthorized />} />
                  <Route path='/pending-approval' element={<PendingApproval />} />
                  <Route path='/service-unavailable' element={<ServiceUnavailable />} />

                  {/* Protected routes */}
                  <Route
                    element={
                      <ProtectedRoute>
                        {/* Spec 286 US4 — a plugged or unverifiable licence replaces the shell (no nav, no data). */}
                        <LicenseGate>
                          <AppLayout />
                        </LicenseGate>
                      </ProtectedRoute>
                    }
                  >
                    {/* Spec 216 T003: the shared child tree lives in AppRoutes.tsx. */}
                    {appChildRoutes}
                  </Route>

                  {/* 404 */}
                  <Route path='*' element={<NotFound />} />
                </Routes>
              </BreadcrumbProvider>
            </BrowserRouter>
        </ThemeProvider>
      </UserPreferencesProvider>
    </QueryClientProvider>
  );
};

/**
 * Dev auth guard: shows role picker when no role is active, then seeds
 * the auth store with the selected persona. No page reload — mirrors
 * the DataMave DevAuthGuard pattern.
 */
const DevAuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const setUser = useAuthStore((s) => s.setUser);
  const setLoading = useAuthStore((s) => s.setLoading);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Read once: a role change reloads the page (handleRoleSelect, DevRoleSwitcher).
  const [role] = React.useState<string | null>(getDevUserRole);

  // Populate auth store on mount and whenever the store is cleared (e.g. idle timeout)
  React.useEffect(() => {
    if (role && !isAuthenticated) {
      setUser({
        id: `dev-${role.toLowerCase()}`,
        displayName: `Dev ${role}`,
        email: `${role.toLowerCase()}@dev.local`,
        role: role.toLowerCase() as any,
        mfaEnabled: false,
      });
      setLoading(false);
    }
  }, [role, isAuthenticated, setUser, setLoading]);

  const handleRoleSelect = React.useCallback((selectedRole: string) => {
    setDevUserRole(selectedRole);
    // A full reload, exactly as DevRoleSwitcher does. 🛑 The earlier no-reload version left two
    // role states: the sidebar followed the picked role, but AuthProvider's store still held the
    // session it read at page load — BEFORE any role existed, so the backend's default, Admin.
    // Picking SuperAdmin (or Viewer) on this page therefore showed "Admin" in the header and the
    // role switcher. The reload makes AuthProvider read the session with the picked role.
    window.location.reload();
  }, []);

  if (!role) {
    return <DevLoginPage onRoleSelect={handleRoleSelect} />;
  }

  return <>{children}</>;
};

export default App;
